<template>
  <div v-if="facet.status === 'loading' && rows.length === 0" :style="{ padding: '6px 4px' }">
    <div v-for="i in [0, 1, 2, 3]" :key="i" :style="{ display: 'flex', alignItems: 'center', gap: '9px', padding: '7px 8px' }">
      <span class="fql-shimmer" :style="{ width: '16px', height: '16px', borderRadius: '5px' }" />
      <span class="fql-shimmer" :style="{ height: '11px', borderRadius: '6px', width: `${60 - i * 10}%` }" />
    </div>
    <div :style="{ display: 'flex', alignItems: 'center', gap: '7px', padding: '2px 10px 4px', fontSize: '11px', color: 'var(--p-surface-400)' }">
      <i class="pi pi-spin pi-spinner" :style="{ fontSize: '11px' }" /> Counting matches…
    </div>
  </div>

  <div v-else-if="facet.status === 'error'" :style="{ padding: '10px 8px', display: 'flex', alignItems: 'center', gap: '9px' }">
    <i class="pi pi-exclamation-triangle" :style="{ color: 'var(--p-status-danger)', fontSize: '12px' }" />
    <span :style="{ fontSize: '12px', color: 'var(--p-text-muted-color)', flex: 1 }">{{ facet.error || 'Couldn’t load values.' }}</span>
    <button
      :style="{ fontSize: '11px', fontWeight: 700, color: 'var(--p-primary-700)', background: 'transparent', border: '1px solid var(--p-content-border-color)', borderRadius: '7px', padding: '3px 9px', cursor: 'pointer', fontFamily: 'inherit' }"
      @click="facet.retry"
    >Retry</button>
  </div>

  <div v-else-if="rows.length === 0" :style="{ padding: '10px 8px', fontSize: '12px', color: 'var(--p-surface-400)', textAlign: 'center' }">
    No values match the current filter
  </div>

  <div v-else :style="listStyle">
    <label
      v-for="{ value, count } in rows"
      :key="value"
      :style="{
        display: 'flex',
        alignItems: 'center',
        gap: '9px',
        padding: '6px 8px',
        borderRadius: '8px',
        cursor: 'pointer',
        background: fieldSlot.values.has(value) ? 'var(--p-highlight-background, color-mix(in srgb, var(--p-primary-500) 12%, transparent))' : 'transparent',
      }"
    >
      <Check :on="fieldSlot.values.has(value)" />
      <input type="checkbox" :checked="fieldSlot.values.has(value)" :style="{ display: 'none' }" @change="search.toggleValue(fieldKey, value)">
      <span
        v-if="emFor(value) && emFor(value).color"
        :style="{ width: '8px', height: '8px', borderRadius: '999px', background: emFor(value).color, flexShrink: 0, opacity: count === 0 && !fieldSlot.values.has(value) ? 0.4 : 1 }"
      />
      <span :style="{ flex: 1, fontSize: '12.5px', color: count === 0 && !fieldSlot.values.has(value) ? 'var(--p-surface-400)' : 'var(--p-text-color)' }">
        {{ (emFor(value) && emFor(value).label) || value }}
      </span>
      <span
        v-if="count != null"
        :style="{ fontSize: '11px', fontFamily: mono, color: count === 0 && !fieldSlot.values.has(value) ? 'var(--p-surface-400)' : 'var(--p-text-muted-color)', fontVariantNumeric: 'tabular-nums' }"
      >{{ count }}</span>
    </label>
  </div>
</template>

<script setup>
// Faceted checkbox list (values + live counts). Ported from fql-search.jsx
// FacetRows. `facet` is the object returned by useFacetValues.
import { computed } from 'vue'
import { mono, listScroll, Check } from './bits.js'

const props = defineProps({
  facet: { type: Object, required: true },
  fieldSlot: { type: Object, required: true },
  fieldKey: { type: String, required: true },
  meta: { type: Object, default: () => ({}) },
  search: { type: Object, required: true },
})

const rows = computed(() => {
  const map = new Map()
  props.facet.values.forEach((o) => map.set(o.value, o.count))
  ;[...props.fieldSlot.values].forEach((v) => {
    if (!map.has(v)) map.set(v, 0)
  })
  return [...map.entries()].map(([value, count]) => ({ value, count }))
})

const listStyle = computed(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '1px',
  ...listScroll,
  opacity: props.facet.status === 'loading' ? 0.5 : 1,
  transition: 'opacity .12s',
}))
const emFor = (value) => (props.meta.enumMeta && props.meta.enumMeta[value]) || null
</script>
